import { useCallback } from "react";

import type { AuthResponseData, ResponseAPI, ServiceError } from "@shapr3d/frontend-lib";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { TeamInvitationSignUpFormData } from "../components/TeamInvitationSelfServeSignUp/TeamInvitationSelfServeSignUp";
import { UMSrequestClient } from "../stores/requestClient";
import type { SignupSource } from "../types";
import { saveLoggedInUser } from "../utils/authUtils";
import { accountQueryKey } from "./useAccount";

export type TeamInvitationsResponseData = {
  userExists: boolean;
  email: string;
  teamName: string;
  redirectURL?: string;
};

export function useInvitation(invitationHash: string | undefined) {
  const getInvitation = useCallback((): Promise<TeamInvitationsResponseData> => {
    return UMSrequestClient.publicRequest({
      method: "GET",
      url: `/teams/self-serve-invitations/${invitationHash}`,
    }).then((res: ResponseAPI) => {
      return res.json<TeamInvitationsResponseData>();
    });
  }, [invitationHash]);

  return useQuery<TeamInvitationsResponseData, ServiceError>({
    queryKey: ["get-invitation", invitationHash],
    queryFn: getInvitation,
    enabled: Boolean(invitationHash),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export type TeamInvitationsAcceptResponseData = {
  email: string;
  teamName: string;
};

export function useAcceptInvitation(invitationHash: string | undefined) {
  const queryClient = useQueryClient();
  const acceptInvitation = useCallback((): Promise<TeamInvitationsAcceptResponseData> => {
    return UMSrequestClient.publicRequest({
      method: "POST",
      url: `/teams/self-serve-invitations/${invitationHash}/accept`,
    }).then((res: ResponseAPI) => {
      return res.json();
    });
  }, [invitationHash]);

  return useMutation<TeamInvitationsAcceptResponseData, ServiceError>({
    mutationKey: ["accept-invitation", invitationHash],
    mutationFn: acceptInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountQueryKey });
    },
  });
}

export function useInvitationSignUp(
  invitationHash: string | undefined,
  { signupSource, isMobile }: { signupSource: SignupSource; isMobile: boolean }
) {
  const submitInvitationSignUp = useCallback(
    (data: TeamInvitationSignUpFormData) => {
      const body = { password: data.password, name: data.name, signupSource, isMobile };
      return UMSrequestClient.publicRequest({
        method: "POST",
        url: `/auth/web/register/team/${invitationHash}`,
        body,
      }).then((res: ResponseAPI) => {
        return res.json<AuthResponseData>();
      });
    },
    [invitationHash, signupSource, isMobile]
  );

  return useMutation({
    mutationFn: submitInvitationSignUp,
    onSuccess: saveLoggedInUser,
  });
}
